import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Activity, BadgeDollarSign, BarChart3, ChevronRight, Cloud, Database, Folder, FileArchive, Gauge,
  HardDrive, Info, KeyRound, Loader2, Pencil, Plus, RefreshCw, Server,
  ShieldCheck, Trash2, TriangleAlert,
} from "lucide-react";
import HostAwsCostTab from "@/components/HostAwsCostTab";
import HostAwStatsTab from "@/components/HostAwStatsTab";

const STATUS_BADGE = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  auth_failed: "bg-red-50 text-red-700 border-red-200",
  unreachable: "bg-red-50 text-red-700 border-red-200",
  error: "bg-red-50 text-red-700 border-red-200",
  unchecked: "bg-zinc-100 text-zinc-500 border-zinc-200",
};

const fmtBytes = (n) => {
  if (n == null) return "—";
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024, i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
};

const errMsg = (e, fallback) => e.response?.data?.detail || fallback;

/* ── Action password bar ────────────────────────────────────────── */
function ActionPwBar({ configured, actionPw, setActionPw, reloadSecurity }) {
  const [setupOpen, setSetupOpen] = useState(false);
  const [curPw, setCurPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [saving, setSaving] = useState(false);

  const savePw = async () => {
    setSaving(true);
    try {
      await api.post("/hosting/security/set", {
        current_password: curPw || null, new_password: newPw,
      });
      toast.success("Action password saved");
      setSetupOpen(false); setCurPw(""); setNewPw("");
      reloadSecurity();
    } catch (e) { toast.error(errMsg(e, "Could not save password")); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/40">
      <CardContent className="p-4 flex flex-wrap items-center gap-3">
        <ShieldCheck className="h-5 w-5 text-amber-700 shrink-0" />
        <div className="text-sm text-amber-900 flex-1 min-w-[240px]">
          <span className="font-semibold">Server admin verification.</span>{" "}
          {configured
            ? "Any change (add/edit/delete servers, suspend accounts, S3 connections) requires the action password."
            : "No action password is set yet — set one before you can add servers or S3 connections."}
        </div>
        {configured && (
          <Input
            type="password" value={actionPw} placeholder="Action password for changes"
            onChange={(e) => setActionPw(e.target.value)}
            className="w-60 bg-white" data-testid="hosting-action-password-input"
          />
        )}
        <Button variant="outline" size="sm" onClick={() => setSetupOpen(true)}
          data-testid="hosting-set-action-password-btn">
          <KeyRound className="h-4 w-4 mr-1" />
          {configured ? "Change password" : "Set action password"}
        </Button>

        <Dialog open={setupOpen} onOpenChange={setSetupOpen}>
          <DialogContent data-testid="hosting-security-dialog">
            <DialogHeader>
              <DialogTitle>{configured ? "Change" : "Set"} the action password</DialogTitle>
              <DialogDescription>
                Known only to server admin staff. Required for every change on this page.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {configured && (
                <Input type="password" placeholder="Current action password"
                  value={curPw} onChange={(e) => setCurPw(e.target.value)}
                  data-testid="hosting-current-pw-input" />
              )}
              <Input type="password" placeholder="New action password (min 8 chars)"
                value={newPw} onChange={(e) => setNewPw(e.target.value)}
                data-testid="hosting-new-pw-input" />
            </div>
            <DialogFooter>
              <Button onClick={savePw} disabled={saving || newPw.length < 8}
                data-testid="hosting-save-pw-btn">
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}

/* ── Token instructions ─────────────────────────────────────────── */
function TokenInstructions() {
  return (
    <div className="rounded-md border border-sky-200 bg-sky-50/60 p-3 text-xs text-sky-900 space-y-1">
      <div className="flex items-center gap-1.5 font-semibold"><Info className="h-3.5 w-3.5" />How to create a WHM API token (recommended — never use your root password)</div>
      <ol className="list-decimal ml-4 space-y-0.5">
        <li>Log in to WHM (e.g. <span className="font-mono">https://host01.example.com:2087</span>)</li>
        <li>Go to <b>Development → Manage API Tokens</b> → <b>Generate Token</b></li>
        <li>Name it (e.g. "Iceberg App") and restrict privileges — for read-only pick <b>List Accounts</b>, <b>Bandwidth Data</b>, <b>Basic System Information</b>; add <b>Account Suspension</b> only if you want suspend/unsuspend from here</li>
        <li>Copy the token (shown once) and paste it below — it is encrypted at rest and never shown again</li>
      </ol>
    </div>
  );
}

/* ── Add / edit server dialog ───────────────────────────────────── */
const EMPTY_SERVER = { name: "", host: "", whm_username: "root", api_token: "", verify_ssl: true, notes: "", client_id: "" };

function ServerDialog({ dialog, setDialog, actionPw, reload, clients }) {
  const [saving, setSaving] = useState(false);
  const [localPw, setLocalPw] = useState("");
  if (!dialog) return null;
  const { mode, form } = dialog;
  const set = (k, v) => setDialog({ ...dialog, form: { ...form, [k]: v } });
  const pwToUse = localPw || actionPw;

  const save = async () => {
    if (!pwToUse) {
      toast.error("Enter the action password to save this server");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        client_id: form.client_id && form.client_id !== "none" ? form.client_id : null,
        action_password: pwToUse,
      };
      if (mode === "add") await api.post("/hosting/servers", payload);
      else await api.put(`/hosting/servers/${dialog.server_id}`, payload);
      toast.success(mode === "add" ? "Server added" : "Server updated");
      setDialog(null); reload();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={() => setDialog(null)}>
      <DialogContent className="max-w-lg" data-testid="hosting-server-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "add" ? "Add host server" : "Edit host server"}</DialogTitle>
          <DialogDescription>WHM (port 2087) connection using an API token.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          <TokenInstructions />
          <Input placeholder="Server name (e.g. Host01 — Image Traders)"
            value={form.name} onChange={(e) => set("name", e.target.value)}
            data-testid="hosting-server-name-input" />
          <Input placeholder="WHM URL or hostname (e.g. https://host01.imagetraders.com.au:2087)"
            value={form.host} onChange={(e) => set("host", e.target.value)}
            data-testid="hosting-server-host-input" />
          <Input placeholder="WHM username (usually root)"
            value={form.whm_username} onChange={(e) => set("whm_username", e.target.value)}
            data-testid="hosting-server-user-input" />
          <Input type="password"
            placeholder={mode === "edit" ? "New API token (leave blank to keep current)" : "WHM API token"}
            value={form.api_token} onChange={(e) => set("api_token", e.target.value)}
            data-testid="hosting-server-token-input" />
          <label className="flex items-center gap-2 text-sm text-zinc-600">
            <input type="checkbox" checked={form.verify_ssl}
              onChange={(e) => set("verify_ssl", e.target.checked)}
              data-testid="hosting-server-ssl-checkbox" />
            Verify SSL certificate (untick only for self-signed certs)
          </label>
          <Textarea placeholder="Notes (optional)" value={form.notes}
            onChange={(e) => set("notes", e.target.value)} rows={2}
            data-testid="hosting-server-notes-input" />
          <div>
            <div className="text-xs font-semibold text-zinc-600 mb-1">
              Assign to client (optional)
            </div>
            <Select
              value={form.client_id || "none"}
              onValueChange={(v) => set("client_id", v === "none" ? "" : v)}
            >
              <SelectTrigger className="h-9" data-testid="hosting-server-client-select">
                <SelectValue placeholder="— unassigned —" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">— unassigned —</SelectItem>
                {[...clients]
                  .sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""))
                  .map((c) => (
                    <SelectItem key={c.client_id} value={c.client_id}>
                      {c.company_name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
              <ShieldCheck className="h-3.5 w-3.5" />Action password required to save
            </div>
            <Input type="password"
              placeholder={actionPw ? "Using password from the banner above (or type to override)" : "Enter the Host Servers action password"}
              value={localPw} onChange={(e) => setLocalPw(e.target.value)}
              data-testid="hosting-server-action-pw-input" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={save} disabled={saving || !form.name || !form.host || !pwToUse}
            data-testid="hosting-server-save-btn">
            {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Accounts table ─────────────────────────────────────────────── */
function AccountsTab({ server, clients, actionPw }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState("");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/hosting/servers/${server.server_id}/accounts`);
      setData(d);
    } catch (e) { toast.error(errMsg(e, "Could not load accounts")); setData({ accounts: [] }); }
    finally { setLoading(false); }
  }, [server.server_id]);

  useEffect(() => { load(); }, [load]);

  const mapClient = async (user, clientId) => {
    try {
      await api.put(`/hosting/servers/${server.server_id}/accounts/${user}/client`,
        { client_id: clientId === "none" ? null : clientId });
      toast.success("Client mapping saved");
      load();
    } catch (e) { toast.error(errMsg(e, "Mapping failed")); }
  };

  const toggleSuspend = async (acct) => {
    const action = acct.suspended ? "unsuspend" : "suspend";
    if (!window.confirm(`${action === "suspend" ? "Suspend" : "Unsuspend"} account "${acct.user}" (${acct.domain})?`)) return;
    setBusy(acct.user);
    try {
      await api.post(`/hosting/servers/${server.server_id}/accounts/${acct.user}/${action}`,
        { action_password: actionPw });
      toast.success(`Account ${action}ed`);
      load();
    } catch (e) { toast.error(errMsg(e, `${action} failed`)); }
    finally { setBusy(""); }
  };

  if (loading && !data) return <div className="p-6 text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading cPanel accounts…</div>;
  const accounts = data?.accounts || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="text-sm text-zinc-500">{accounts.length} cPanel account{accounts.length === 1 ? "" : "s"}</div>
        <Button variant="outline" size="sm" onClick={load} data-testid="hosting-accounts-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-1 ${loading ? "animate-spin" : ""}`} />Refresh
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm" data-testid="hosting-accounts-table">
          <thead className="bg-zinc-50 text-zinc-500 text-left">
            <tr>
              <th className="p-2 font-medium">Domain</th>
              <th className="p-2 font-medium">User</th>
              <th className="p-2 font-medium">Plan</th>
              <th className="p-2 font-medium">Disk</th>
              <th className="p-2 font-medium">Status</th>
              <th className="p-2 font-medium">Client</th>
              <th className="p-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => (
              <tr key={a.user} className="border-t hover:bg-zinc-50/50">
                <td className="p-2">
                  <button
                    className="font-medium text-sky-700 hover:text-sky-900 hover:underline text-left"
                    onClick={() => navigate(`/host-servers/${server.server_id}/accounts/${a.user}`)}
                    data-testid={`hosting-account-link-${a.user}`}>
                    {a.domain}
                  </button>
                </td>
                <td className="p-2 font-mono text-xs">{a.user}</td>
                <td className="p-2 text-xs">{a.plan}</td>
                <td className="p-2 text-xs">{a.diskused} / {a.disklimit}</td>
                <td className="p-2">
                  {a.suspended
                    ? <Badge className="bg-red-50 text-red-700 border-red-200" variant="outline">suspended</Badge>
                    : <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200" variant="outline">active</Badge>}
                </td>
                <td className="p-2">
                  <Select value={a.client_id || "none"} onValueChange={(v) => mapClient(a.user, v)}>
                    <SelectTrigger className="h-8 w-44 text-xs" data-testid={`hosting-account-client-${a.user}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— unmapped —</SelectItem>
                      {clients.map((c) => (
                        <SelectItem key={c.client_id} value={c.client_id}>{c.company_name || c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-2 text-right">
                  <Button variant="outline" size="sm" disabled={busy === a.user}
                    onClick={() => toggleSuspend(a)}
                    data-testid={`hosting-account-suspend-${a.user}`}>
                    {busy === a.user ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : (a.suspended ? "Unsuspend" : "Suspend")}
                  </Button>
                </td>
              </tr>
            ))}
            {!accounts.length && (
              <tr><td colSpan={7} className="p-6 text-center text-zinc-400">No accounts returned.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Bandwidth tab ──────────────────────────────────────────────── */
function BandwidthTab({ server }) {
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(
        `/hosting/servers/${server.server_id}/bandwidth`, { params: { month, year } });
      setData(d);
    } catch (e) { toast.error(errMsg(e, "Could not load bandwidth")); setData(null); }
    finally { setLoading(false); }
  }, [server.server_id, month, year]);

  useEffect(() => { load(); }, [load]);

  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const years = [year - 2, year - 1, year, now.getFullYear()].filter((v, i, a) => a.indexOf(v) === i).sort();
  const max = Math.max(1, ...(data?.accounts || []).map((a) => a.totalbytes));

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
          <SelectTrigger className="h-8 w-28" data-testid="hosting-bw-month-select"><SelectValue /></SelectTrigger>
          <SelectContent>{months.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
          <SelectTrigger className="h-8 w-24" data-testid="hosting-bw-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
        </Select>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />}
        {data && <div className="ml-auto text-sm text-zinc-600">Total: <b data-testid="hosting-bw-total">{fmtBytes(data.total_bytes)}</b></div>}
      </div>
      <div className="space-y-1.5" data-testid="hosting-bw-list">
        {(data?.accounts || []).map((a) => (
          <div key={a.user} className="flex items-center gap-3 text-sm">
            <div className="w-56 truncate">{a.domain} <span className="text-zinc-400 text-xs font-mono">({a.user})</span></div>
            <div className="flex-1 h-3 rounded bg-zinc-100 overflow-hidden">
              <div className="h-full bg-sky-500 rounded" style={{ width: `${Math.max(1, (a.totalbytes / max) * 100)}%` }} />
            </div>
            <div className="w-20 text-right text-xs text-zinc-600">{fmtBytes(a.totalbytes)}</div>
          </div>
        ))}
        {data && !data.accounts.length && <div className="p-6 text-center text-zinc-400 text-sm">No bandwidth data for this period.</div>}
      </div>
    </div>
  );
}

/* ── Health tab ─────────────────────────────────────────────────── */
function HealthTab({ server }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/hosting/servers/${server.server_id}/health`);
      setData(d);
    } catch (e) { toast.error(errMsg(e, "Could not load health")); setData(null); }
    finally { setLoading(false); }
  }, [server.server_id]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="p-6 text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Reading server health…</div>;
  if (!data) return <div className="p-6 text-sm text-zinc-400">No health data.</div>;

  return (
    <div className="space-y-4" data-testid="hosting-health-panel">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-3">
          <div className="text-xs text-zinc-500">Hostname</div>
          <div className="text-sm font-semibold truncate">{data.hostname || "—"}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-zinc-500">WHM version</div>
          <div className="text-sm font-semibold">{data.whm_version || "—"}</div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-zinc-500">Load (1m / 5m / 15m)</div>
          <div className="text-sm font-semibold">
            {data.loadavg ? `${data.loadavg.one} / ${data.loadavg.five} / ${data.loadavg.fifteen}` : "—"}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-3">
          <div className="text-xs text-zinc-500">Services running</div>
          <div className="text-sm font-semibold">
            {data.services ? `${data.services.filter((s) => s.running).length} / ${data.services.length}` : "—"}
          </div>
        </CardContent></Card>
      </div>
      {data.services && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {data.services.map((s) => (
            <div key={s.name} className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs ${s.running ? "border-emerald-200 bg-emerald-50/50" : "border-red-200 bg-red-50/50"}`}>
              <span className={`h-2 w-2 rounded-full ${s.running ? "bg-emerald-500" : "bg-red-500"}`} />
              <span className="truncate">{s.display}</span>
            </div>
          ))}
        </div>
      )}
      <Button variant="outline" size="sm" onClick={load} data-testid="hosting-health-refresh-btn">
        <RefreshCw className="h-4 w-4 mr-1" />Refresh
      </Button>
    </div>
  );
}

/* ── Servers tab ────────────────────────────────────────────────── */
function ServersTab({ actionPw, clients }) {
  const [servers, setServers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [checking, setChecking] = useState("");
  const [filterClient, setFilterClient] = useState("all");

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/hosting/servers");
      setServers(data);
      setSelected((cur) => cur
        ? data.find((s) => s.server_id === cur.server_id) || null
        : (data.find((s) => s.is_default) || data[0] || null));
    } catch { /* toast handled globally */ }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const setDefaultServer = async (s, val) => {
    try {
      await api.put(`/hosting/servers/${s.server_id}/default`, { is_default: val });
      toast.success(val ? `"${s.name}" now opens by default` : "Default cleared");
      reload();
    } catch (e) { toast.error(errMsg(e, "Could not set default server")); }
  };

  const check = async (s) => {
    setChecking(s.server_id);
    try {
      const { data } = await api.post(`/hosting/servers/${s.server_id}/check`);
      if (data.status === "ok") toast.success(`Connected — WHM ${data.version}`);
      else toast.error(data.detail || "Connection failed");
      reload();
    } catch (e) { toast.error(errMsg(e, "Check failed")); }
    finally { setChecking(""); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete server "${s.name}"? This only removes it from the app.`)) return;
    let pw = actionPw;
    if (!pw) {
      pw = window.prompt("Enter the Host Servers action password to confirm delete:") || "";
      if (!pw) return;
    }
    try {
      await api.request({ method: "delete", url: `/hosting/servers/${s.server_id}`, data: { action_password: pw } });
      toast.success("Server removed");
      setSelected(null); reload();
    } catch (e) { toast.error(errMsg(e, "Delete failed")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-sm text-zinc-500">{servers.length} server{servers.length === 1 ? "" : "s"} registered</div>
        <div className="flex items-center gap-2">
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="h-8 w-56 text-xs" data-testid="hosting-server-filter-client">
              <SelectValue placeholder="Filter by client" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All clients</SelectItem>
              <SelectItem value="unassigned">— unassigned —</SelectItem>
              {[...clients]
                .sort((a, b) => (a.company_name || "").localeCompare(b.company_name || ""))
                .map((c) => (
                  <SelectItem key={c.client_id} value={c.client_id}>
                    {c.company_name}
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>
          <Button size="sm" onClick={() => setDialog({ mode: "add", form: { ...EMPTY_SERVER } })}
            data-testid="hosting-add-server-btn">
            <Plus className="h-4 w-4 mr-1" />Add host server
          </Button>
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {servers
          .filter((s) => {
            if (filterClient === "all") return true;
            if (filterClient === "unassigned") return !s.client_id;
            return s.client_id === filterClient;
          })
          .map((s) => (
          <Card key={s.server_id}
            className={`cursor-pointer transition-colors ${selected?.server_id === s.server_id ? "border-sky-400 ring-1 ring-sky-200" : "hover:border-zinc-300"}`}
            onClick={() => setSelected(s)} data-testid={`hosting-server-card-${s.server_id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Server className="h-4 w-4 text-zinc-400 shrink-0" />
                  <div className="font-semibold truncate">{s.name}</div>
                </div>
                <Badge variant="outline" className={STATUS_BADGE[s.status] || STATUS_BADGE.unchecked}>
                  {s.status}
                </Badge>
              </div>
              <div className="text-xs text-zinc-500 font-mono truncate">{s.host}:{s.port}</div>
              <div className="flex items-center gap-1.5">
                {s.client_name ? (
                  <Badge variant="outline"
                    className="bg-sky-50 text-sky-700 border-sky-200 text-[10px] font-medium"
                    data-testid={`hosting-server-client-badge-${s.server_id}`}>
                    <ShieldCheck className="h-3 w-3 mr-1" />{s.client_name}
                  </Badge>
                ) : (
                  <Badge variant="outline"
                    className="bg-zinc-50 text-zinc-500 border-zinc-200 text-[10px]"
                    data-testid={`hosting-server-client-badge-${s.server_id}`}>
                    unassigned
                  </Badge>
                )}
              </div>
              {s.status_detail && <div className="text-xs text-zinc-500 line-clamp-2">{s.status_detail}</div>}
              <label
                className="flex items-center gap-1.5 text-[11px] text-zinc-500 cursor-pointer w-fit"
                onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={!!s.is_default}
                  onChange={(e) => setDefaultServer(s, e.target.checked)}
                  data-testid={`hosting-default-checkbox-${s.server_id}`} />
                Open website list by default
              </label>
              <div className="flex items-center gap-1.5 pt-1">
                <Button variant="outline" size="sm" disabled={checking === s.server_id}
                  onClick={(e) => { e.stopPropagation(); check(s); }}
                  data-testid={`hosting-check-server-${s.server_id}`}>
                  {checking === s.server_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
                  <span className="ml-1">Test</span>
                </Button>
                <Button variant="ghost" size="sm"
                  onClick={(e) => { e.stopPropagation(); setDialog({ mode: "edit", server_id: s.server_id, form: { ...EMPTY_SERVER, ...s, api_token: "" } }); }}
                  data-testid={`hosting-edit-server-${s.server_id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600"
                  onClick={(e) => { e.stopPropagation(); remove(s); }}
                  data-testid={`hosting-delete-server-${s.server_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {!servers.length && (
          <Card className="md:col-span-2 lg:col-span-3 border-dashed">
            <CardContent className="p-10 text-center text-zinc-400 text-sm">
              <Server className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No host servers yet. Add your first WHM server — you'll need an API token
              (WHM → Development → Manage API Tokens).
            </CardContent>
          </Card>
        )}
      </div>

      {selected && (
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <HardDrive className="h-4 w-4 text-zinc-400" />
              <div className="font-semibold">{selected.name}</div>
              <ChevronRight className="h-4 w-4 text-zinc-300" />
              <div className="text-sm text-zinc-500 font-mono">{selected.host}</div>
            </div>
            <Tabs defaultValue="accounts">
              <TabsList>
                <TabsTrigger value="accounts" data-testid="hosting-tab-accounts"><Database className="h-3.5 w-3.5 mr-1" />Accounts</TabsTrigger>
                <TabsTrigger value="bandwidth" data-testid="hosting-tab-bandwidth"><Gauge className="h-3.5 w-3.5 mr-1" />Bandwidth</TabsTrigger>
                <TabsTrigger value="health" data-testid="hosting-tab-health"><Activity className="h-3.5 w-3.5 mr-1" />Health</TabsTrigger>
              </TabsList>
              <TabsContent value="accounts" className="pt-3">
                <AccountsTab server={selected} clients={clients} actionPw={actionPw} />
              </TabsContent>
              <TabsContent value="bandwidth" className="pt-3">
                <BandwidthTab server={selected} />
              </TabsContent>
              <TabsContent value="health" className="pt-3">
                <HealthTab server={selected} />
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      <ServerDialog dialog={dialog} setDialog={setDialog} actionPw={actionPw} reload={reload} clients={clients} />
    </div>
  );
}

/* ── S3 backups tab ─────────────────────────────────────────────── */
const EMPTY_S3 = { name: "", bucket: "", region: "ap-southeast-2", prefix: "", access_key: "", secret_key: "" };

function S3BrowseDialog({ conn, onClose }) {
  const [prefix, setPrefix] = useState(conn.prefix || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (p) => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/hosting/s3/${conn.conn_id}/browse`, { params: { prefix: p } });
      setData(d); setPrefix(d.prefix);
    } catch (e) { toast.error(errMsg(e, "Browse failed")); }
    finally { setLoading(false); }
  }, [conn.conn_id]);

  useEffect(() => { load(prefix); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const up = () => {
    const parts = prefix.replace(/\/$/, "").split("/");
    parts.pop();
    load(parts.length ? `${parts.join("/")}/` : "");
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="s3-browse-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Cloud className="h-4 w-4" />{conn.bucket}</DialogTitle>
          <DialogDescription className="font-mono text-xs">/{prefix || ""}</DialogDescription>
        </DialogHeader>
        <div className="max-h-[55vh] overflow-y-auto space-y-1">
          {loading && <div className="p-4 text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Loading…</div>}
          {!loading && prefix && (
            <button onClick={up} className="w-full text-left px-2 py-1.5 rounded hover:bg-zinc-50 text-sm text-zinc-500" data-testid="s3-browse-up-btn">← up one level</button>
          )}
          {!loading && (data?.folders || []).map((f) => (
            <button key={f} onClick={() => load(f)}
              className="w-full flex items-center gap-2 text-left px-2 py-1.5 rounded hover:bg-zinc-50 text-sm"
              data-testid={`s3-folder-${f}`}>
              <Folder className="h-4 w-4 text-amber-500 shrink-0" />
              <span className="truncate">{f.slice(prefix.length).replace(/\/$/, "")}</span>
            </button>
          ))}
          {!loading && (data?.files || []).map((f) => (
            <div key={f.key} className="flex items-center gap-2 px-2 py-1.5 text-sm">
              <FileArchive className="h-4 w-4 text-zinc-400 shrink-0" />
              <span className="truncate flex-1">{f.key.slice(prefix.length)}</span>
              <span className="text-xs text-zinc-500 w-20 text-right">{fmtBytes(f.size)}</span>
              <span className="text-xs text-zinc-400 w-36 text-right">{new Date(f.last_modified).toLocaleString()}</span>
            </div>
          ))}
          {!loading && data && !data.folders.length && !data.files.length && (
            <div className="p-6 text-center text-zinc-400 text-sm">Empty.</div>
          )}
          {data?.truncated && <div className="text-xs text-zinc-400 px-2">Showing first 500 items…</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function S3SummaryPanel({ conn }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/hosting/s3/${conn.conn_id}/summary`);
      setData(d);
    } catch (e) { toast.error(errMsg(e, "Summary failed")); }
    finally { setLoading(false); }
  };

  return (
    <div className="mt-2">
      {!data && (
        <Button variant="outline" size="sm" onClick={load} disabled={loading}
          data-testid={`s3-summary-btn-${conn.conn_id}`}>
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Gauge className="h-3.5 w-3.5 mr-1" />}
          Backup freshness check
        </Button>
      )}
      {data && (
        <div className="space-y-1" data-testid={`s3-summary-panel-${conn.conn_id}`}>
          <div className="text-xs text-zinc-500">{data.scanned_objects} objects scanned · stale = no backup in {data.stale_days} days</div>
          {data.groups.map((g) => (
            <div key={g.name} className={`flex items-center gap-2 rounded border px-2 py-1.5 text-xs ${g.stale ? "border-red-200 bg-red-50/60" : "border-emerald-200 bg-emerald-50/40"}`}>
              {g.stale ? <TriangleAlert className="h-3.5 w-3.5 text-red-600 shrink-0" /> : <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" />}
              <span className="font-medium truncate flex-1">{g.name}</span>
              <span className="text-zinc-500">{g.count} files · {fmtBytes(g.total_size)}</span>
              <span className={g.stale ? "text-red-700 font-semibold" : "text-zinc-500"}>
                {g.latest ? `latest ${g.age_days}d ago` : "no backups"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function S3Tab({ actionPw }) {
  const [conns, setConns] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [browse, setBrowse] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState("");
  const [dialogPw, setDialogPw] = useState("");

  const reload = useCallback(async () => {
    try { const { data } = await api.get("/hosting/s3"); setConns(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const save = async () => {
    const pw = dialogPw || actionPw;
    if (!pw) { toast.error("Enter the action password to save this connection"); return; }
    setSaving(true);
    try {
      await api.post("/hosting/s3", { ...dialog, action_password: pw });
      toast.success("S3 connection added");
      setDialog(null); setDialogPw(""); reload();
    } catch (e) { toast.error(errMsg(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const check = async (c) => {
    setChecking(c.conn_id);
    try {
      const { data } = await api.post(`/hosting/s3/${c.conn_id}/check`);
      if (data.status === "ok") toast.success("Bucket reachable");
      else toast.error(data.detail || "Check failed");
      reload();
    } catch (e) { toast.error(errMsg(e, "Check failed")); }
    finally { setChecking(""); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Remove S3 connection "${c.name}"?`)) return;
    let pw = actionPw;
    if (!pw) {
      pw = window.prompt("Enter the Host Servers action password to confirm delete:") || "";
      if (!pw) return;
    }
    try {
      await api.request({ method: "delete", url: `/hosting/s3/${c.conn_id}`, data: { action_password: pw } });
      toast.success("Removed"); reload();
    } catch (e) { toast.error(errMsg(e, "Delete failed")); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-zinc-500">
          Connect read-only IAM keys scoped to your backup buckets.
        </div>
        <Button size="sm" onClick={() => { setDialog({ ...EMPTY_S3 }); setDialogPw(""); }} data-testid="s3-add-btn">
          <Plus className="h-4 w-4 mr-1" />Add S3 connection
        </Button>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {conns.map((c) => (
          <Card key={c.conn_id} data-testid={`s3-card-${c.conn_id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Cloud className="h-4 w-4 text-zinc-400 shrink-0" />
                  <div className="font-semibold truncate">{c.name}</div>
                </div>
                <Badge variant="outline" className={STATUS_BADGE[c.status] || STATUS_BADGE.unchecked}>{c.status}</Badge>
              </div>
              <div className="text-xs text-zinc-500 font-mono truncate">s3://{c.bucket}/{c.prefix || ""} · {c.region}</div>
              {c.status_detail && <div className="text-xs text-red-600 line-clamp-2">{c.status_detail}</div>}
              <div className="flex items-center gap-1.5">
                <Button variant="outline" size="sm" disabled={checking === c.conn_id}
                  onClick={() => check(c)} data-testid={`s3-check-${c.conn_id}`}>
                  {checking === c.conn_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Activity className="h-3.5 w-3.5" />}
                  <span className="ml-1">Test</span>
                </Button>
                <Button variant="outline" size="sm" onClick={() => setBrowse(c)} data-testid={`s3-browse-${c.conn_id}`}>
                  <Folder className="h-3.5 w-3.5 mr-1" />Browse
                </Button>
                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600"
                  onClick={() => remove(c)} data-testid={`s3-delete-${c.conn_id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
              <S3SummaryPanel conn={c} />
            </CardContent>
          </Card>
        ))}
        {!conns.length && (
          <Card className="md:col-span-2 border-dashed">
            <CardContent className="p-10 text-center text-zinc-400 text-sm">
              <Cloud className="h-8 w-8 mx-auto mb-2 opacity-40" />
              No S3 backup connections yet. Create an IAM user with read-only access
              (s3:ListBucket + s3:GetObject) scoped to the backup bucket, then add its keys here.
            </CardContent>
          </Card>
        )}
      </div>

      {dialog && (
        <Dialog open onOpenChange={() => { setDialog(null); setDialogPw(""); }}>
          <DialogContent data-testid="s3-add-dialog">
            <DialogHeader>
              <DialogTitle>Add S3 backup connection</DialogTitle>
              <DialogDescription>
                Use an IAM user restricted to read-only (ListBucket / GetObject) on the backup bucket.
                Keys are encrypted at rest.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Name (e.g. Host01 nightly backups)" value={dialog.name}
                onChange={(e) => setDialog({ ...dialog, name: e.target.value })} data-testid="s3-name-input" />
              <Input placeholder="Bucket name" value={dialog.bucket}
                onChange={(e) => setDialog({ ...dialog, bucket: e.target.value })} data-testid="s3-bucket-input" />
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Region (e.g. ap-southeast-2)" value={dialog.region}
                  onChange={(e) => setDialog({ ...dialog, region: e.target.value })} data-testid="s3-region-input" />
                <Input placeholder="Prefix / folder (optional)" value={dialog.prefix}
                  onChange={(e) => setDialog({ ...dialog, prefix: e.target.value })} data-testid="s3-prefix-input" />
              </div>
              <Input placeholder="AWS Access Key ID" value={dialog.access_key}
                onChange={(e) => setDialog({ ...dialog, access_key: e.target.value })} data-testid="s3-access-key-input" />
              <Input type="password" placeholder="AWS Secret Access Key" value={dialog.secret_key}
                onChange={(e) => setDialog({ ...dialog, secret_key: e.target.value })} data-testid="s3-secret-key-input" />
              <div className="rounded-md border border-amber-200 bg-amber-50/40 p-3 space-y-2">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-amber-900">
                  <ShieldCheck className="h-3.5 w-3.5" />Action password required to save
                </div>
                <Input type="password"
                  placeholder={actionPw ? "Using password from the banner above (or type to override)" : "Enter the Host Servers action password"}
                  value={dialogPw} onChange={(e) => setDialogPw(e.target.value)}
                  data-testid="s3-action-pw-input" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving || !dialog.name || !dialog.bucket || !dialog.access_key || !dialog.secret_key || !(dialogPw || actionPw)}
                data-testid="s3-save-btn">
                {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {browse && <S3BrowseDialog conn={browse} onClose={() => setBrowse(null)} />}
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────── */
export default function HostServersPage() {
  const [security, setSecurity] = useState({ configured: true });
  const [actionPw, setActionPw] = useState("");
  const [clients, setClients] = useState([]);

  const reloadSecurity = useCallback(() => {
    api.get("/hosting/security/status").then((r) => setSecurity(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    reloadSecurity();
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [reloadSecurity]);

  return (
    <div className="p-6 space-y-4 max-w-6xl" data-testid="host-servers-page">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Server className="h-6 w-6 text-sky-600" />Host Servers
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          WHM/cPanel servers (accounts, bandwidth, health) and Amazon S3 backup buckets.
          Connections use API tokens & IAM keys — encrypted at rest, never your passwords.
        </p>
      </div>

      <ActionPwBar configured={security.configured} actionPw={actionPw}
        setActionPw={setActionPw} reloadSecurity={reloadSecurity} />

      <Tabs defaultValue="servers">
        <TabsList>
          <TabsTrigger value="servers" data-testid="hosting-main-tab-servers"><Server className="h-4 w-4 mr-1" />WHM Servers</TabsTrigger>
          <TabsTrigger value="s3" data-testid="hosting-main-tab-s3"><Cloud className="h-4 w-4 mr-1" />S3 Backups</TabsTrigger>
          <TabsTrigger value="aws-cost" data-testid="hosting-main-tab-aws-cost"><BadgeDollarSign className="h-4 w-4 mr-1" />AWS Cost</TabsTrigger>
          <TabsTrigger value="awstats" data-testid="hosting-main-tab-awstats"><BarChart3 className="h-4 w-4 mr-1" />AWStats</TabsTrigger>
        </TabsList>
        <TabsContent value="servers" className="pt-3">
          <ServersTab actionPw={actionPw} clients={clients} />
        </TabsContent>
        <TabsContent value="s3" className="pt-3">
          <S3Tab actionPw={actionPw} />
        </TabsContent>
        <TabsContent value="aws-cost" className="pt-3">
          <HostAwsCostTab />
        </TabsContent>
        <TabsContent value="awstats" className="pt-3">
          <HostAwStatsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
